import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, MaskedLines } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Independent by design.",
    body:
      "John Campbell Insurance answers to you — not a single carrier. We compare more than 25 A-rated or better insurance carriers to engineer coverage around your life, not a sales quota.",
  },
  {
    n: "02",
    title: "Advice that outlives the policy.",
    body:
      "A policy is a promise measured in decades. We review your coverage as your family grows, income changes, and priorities shift — so protection never falls out of step with life.",
  },
  {
    n: "03",
    title: "Clarity over complexity.",
    body:
      "Riders, conversions, cash value, underwriting — we translate the fine print into plain language, then hand you a recommendation you can actually understand and trust.",
  },
];

const PORTRAIT = "https://images.pexels.com/photos/5638417/pexels-photo-5638417.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900";

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" data-testid="about-section" className="relative bg-[#030303] py-16 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">The Approach</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <Reveal className="mb-12 max-w-4xl">
          <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLines
              lines={[
                <>We don't sell insurance.</>,
                <>
                  We build <span className="italic text-[#d4af37]">certainty</span>.
                </>,
              ]}
            />
          </h2>
        </Reveal>

        <div ref={ref} className="relative grid lg:grid-cols-[1fr_0.85fr] gap-16 lg:gap-24 items-start">
          {/* chapters */}
          <div className="order-2 lg:order-1">
            {CHAPTERS.map((c, i) => (
              <Reveal
                key={c.n}
                delay={i * 0.08}
                className="grid grid-cols-[auto_1fr] gap-6 lg:gap-10 py-10 border-t border-white/10"
              >
                <span className="mono text-sm text-[#d4af37] pt-2">{c.n}</span>
                <div>
                  <h3 className="serif text-2xl lg:text-3xl text-[#f4f0ea] mb-3">{c.title}</h3>
                  <p className="text-[#a3a3a3] leading-relaxed tracking-wide max-w-xl">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* sticky parallax image */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-28">
            <div className="relative overflow-hidden rounded-sm">
              <motion.img
                style={{ y }}
                src={PORTRAIT}
                alt="A family together"
                className="w-full h-[420px] lg:h-[560px] object-cover scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/30 mix-blend-multiply" />
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#030303] to-transparent" />
            </div>
            <div className="mt-6 flex items-baseline justify-between">
              <p className="serif italic text-xl text-[#f4f0ea]">Est. for families like yours</p>
              <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#737373]">Trusted Advice</span>
            </div>
          </div>
        </div>

        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-28 border-t border-white/10 pt-14">
          {[
            ["25+", "A-rated or better carriers"],
            ["$100M+", "Coverage placed"],
            ["24 hr", "Quote turnaround"],
            ["100%", "Independent"],
          ].map(([stat, label], i) => (
            <Reveal key={label} delay={i * 0.07}>
              <div className="serif text-4xl lg:text-5xl text-[#d4af37] mb-2">{stat}</div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">{label}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
